import { FC, useMemo } from 'react';
import { Layer, LayerTypeEnum, metrics, useLayerStore } from './layerStore';
import { Button, Tooltip, styled } from '@mui/material';
import { ControlSection } from '../ui/ControlSection';
import { InfoOutlined, WarningAmberOutlined } from '@mui/icons-material';

export const LayerToggles: FC = () => {
  const { layerState, modelType: type } = useLayerStore();

  const threeDModelToggles = useMemo(
    () =>
      Object.entries(layerState).filter(
        ([_, value]) =>
          value.modelType === type &&
          value.layerType === LayerTypeEnum.ThreeDModel
      ),
    [layerState, type]
  );

  const mapToggles = useMemo(
    () =>
      Object.entries(layerState).filter(
        ([_, value]) =>
          value.modelType === type && value.layerType === LayerTypeEnum.Map
      ),
    [layerState, type]
  );

  const coronalToggles = useMemo(
    () =>
      Object.entries(layerState).filter(
        ([_, value]) =>
          value.modelType === type && value.layerType === LayerTypeEnum.General
      ),
    [layerState, type]
  );

  const sagittalToggles = useMemo(
    () =>
      Object.entries(layerState).filter(
        ([_, value]) =>
          value.modelType === type && value.layerType === LayerTypeEnum.Sagittal
      ),
    [layerState, type]
  );

  return (
    <LayerControlsContainer>
      <LayerToggleGroup
        title='3D Model'
        layers={threeDModelToggles}
        showToggleAll={false}
      />
      <LayerToggleGroup title='Map' layers={mapToggles} showToggleAll={false} />
      <LayerToggleGroup title='Coronal Metrics' layers={coronalToggles} />
      <LayerToggleGroup
        title='Sagittal Metrics'
        layers={sagittalToggles}
        warning={true}
      />
    </LayerControlsContainer>
  );
};

interface LayerToggleGroupProps {
  title: string;
  layers: [string, Layer][];
  showToggleAll?: boolean;
  warning?: boolean;
}

const LayerToggleGroup: FC<LayerToggleGroupProps> = ({
  title,
  layers,
  showToggleAll = true,
  warning,
}) => {
  const { setLayerToggleState, toggleLayer, highlightLayer } = useLayerStore();

  const { someVisible, allVisible } = useMemo(() => {
    const someVisible = layers.some(([_, value]) => value.visible);
    const allVisible = layers.every(([_, value]) => value.visible);
    return { someVisible, allVisible };
  }, [layers]);

  const toggleAll = () => {
    layers.forEach(([key]) => {
      setLayerToggleState(key, !allVisible);
    });
  };

  if (layers.length === 0) return null;

  const titleAction = showToggleAll ? (
    <Button
      size='small'
      variant={allVisible ? 'toggleActive' : 'toggleInactive'}
      onClick={toggleAll}
      sx={{
        fontSize: '11px',
        fontWeight: 600,
        minWidth: 'auto',
        padding: '4px 8px',
        borderRadius: '6px',
      }}
    >
      {allVisible ? 'Hide All' : someVisible ? 'Show All' : 'Show All'}
    </Button>
  ) : undefined;

  const warningAction = (
    <Tooltip title='Supplementary Feature - for informational use only: These metrics are derived from surface measurements and should not be used independently for diagnosis or treatment decisions'>
      <WarningAmberOutlined
        fontSize='small'
        sx={{ marginTop: '-0.25rem', cursor: 'pointer' }}
      />
    </Tooltip>
  );

  return (
    <ControlSection
      title={title}
      titleAction={titleAction}
      warning={warning}
      warningAction={warningAction}
    >
      <LayerButtonGrid>
        {layers.map(([key, value]) => (
          <LayerToggleItem
            key={key}
            layer={value}
            toggleLayer={toggleLayer}
            highlightLayer={highlightLayer}
          />
        ))}
      </LayerButtonGrid>
    </ControlSection>
  );
};

interface LayerToggleItemProps {
  layer: Layer;
  toggleLayer: (key: string) => void;
  highlightLayer: (key: string | null) => void;
}

const LayerToggleItem: FC<LayerToggleItemProps> = ({
  layer,
  toggleLayer,
  highlightLayer,
}) => {
  const { key, visible } = layer;
  const displayName = metrics[key]?.toggleName || key;
  const description = metrics[key]?.description;

  const handleClick = () => {
    toggleLayer(key);
  };

  return (
    <Button
      fullWidth
      size='small'
      variant={visible ? 'toggleActive' : 'toggleInactive'}
      onClick={handleClick}
      onMouseEnter={() => highlightLayer(key)}
      onMouseLeave={() => highlightLayer(null)}
      endIcon={
        description ? (
          <Tooltip title={description} placement='right'>
            <InfoOutlined fontSize='small' />
          </Tooltip>
        ) : undefined
      }
    >
      {displayName}
    </Button>
  );
};

// Styled Components
const LayerControlsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const LayerButtonGrid = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));
